import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import 'prosemirror-view/style/prosemirror.css'; // Prevents tiptap console warning CSS white-space property not set
import './providers/i18n/i18nProvider'; // init i18n before rendering
import { GlobalStyles } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs';
import { CosmosQueryClientProvider } from 'providers/clients/CosmosQueryClientProvider';
import { CosmosPlayerContextProvider } from 'providers/contexts/CosmosPlayerContext';
import React, { lazy, memo, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import BloxLoadingSplash from './components/generic/BloxLoadingSplash';
import { SetupAuthentication } from './providers/contexts/CosmosAuthEnvContext';
import { CosmosProvider } from './providers/contexts/CosmosContext';
import { CosmosRuntimeProvider } from './providers/contexts/CosmosRuntimeContext';
import { CosmosRAProviders } from './providers/contexts/CosmosRAProvidersContext';
import CallbacksHandler from './screens/callbacks/CallbacksHandler';
import { AnalyticsProvider } from 'providers/contexts/analytics/AnalyticsContext';
import { CosmosLayoutProvider } from 'providers/contexts/CosmosLayoutContext';
import { CosmosDictationKeyProvider } from 'providers/contexts/CosmosDictationKeyContext';
import { globalStyles } from './themes/constants';

const LazyCosmosReactAdmin = lazy(() => import('./App'));
const hoistedGlobalStyles = <GlobalStyles styles={globalStyles} />;

const AppWithProviders = memo(() => (
  <Suspense fallback={<BloxLoadingSplash message="Loading..." />}>
    {hoistedGlobalStyles}
    <CosmosRuntimeProvider>
      <CosmosQueryClientProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <SetupAuthentication>
            <React.StrictMode>
              <CosmosLayoutProvider>
                <CosmosDictationKeyProvider>
                  <CosmosProvider>
                    <CosmosRAProviders>
                      <AnalyticsProvider>
                        <CosmosPlayerContextProvider>
                          <LazyCosmosReactAdmin />
                        </CosmosPlayerContextProvider>
                      </AnalyticsProvider>
                    </CosmosRAProviders>
                  </CosmosProvider>
                </CosmosDictationKeyProvider>
              </CosmosLayoutProvider>
            </React.StrictMode>
          </SetupAuthentication>
        </LocalizationProvider>
      </CosmosQueryClientProvider>
    </CosmosRuntimeProvider>
  </Suspense>
));
AppWithProviders.displayName = 'AppWithProviders';

// Create the router with routes configuration
const router = createBrowserRouter(
  [
    {
      /**
       * This route handles redirect callbacks.
       * eg: www.admin.bloxcms.com/callbacks
       */
      path: 'callbacks',
      element: <CallbacksHandler />,
    },
    {
      /**
       * notes: the ? after :clientSite for optional segment
       * and the CosmosProvider will 'nav' to the clients
       * site if the clientSite is not provided. Alternatively
       * if it is provided, CosmosProvider should still check
       * if the clientSite is valid and redirect or keep it.
       * The /* catches all remaining segments and should
       * eventually funnel/route down to the CosmosReactAdmin...
       * eg: admin.bloxcms.com/ (no site, redir user default)
       * eg: admin.bloxcms.com/example.net/... (client site segment)
       */
      path: ':clientSite?/*',
      element: <AppWithProviders />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

// Root component to render
const RootApp = memo(() => {
  return (
    <Suspense fallback={<BloxLoadingSplash message="Loading..." />}>
      <RouterProvider
        router={router}
        fallbackElement={<BloxLoadingSplash message="Loading..." />}
        future={{
          v7_startTransition: true,
        }}
      />
    </Suspense>
  );
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<RootApp />);

// Debugging lines can remain commented out here...
// import { Log } from 'oidc-client-ts';
// Log.setLogger(console);  Log.setLevel(Log.DEBUG);
